import {
  getUsersResponseSchema,
  getMeaningfulGigsUsersResponseSchema,
  updateUserAdminNoteResponseSchema,
  updateUserRoleResponseSchema,
} from "@mg/schemas/src/christo/catalyst";
import { type User, userSchema } from "@mg/schemas/src/commons/user";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

export async function getUsers(roles?: string[]) {
  const queryParams = new URLSearchParams();

  if (roles) {
    roles?.forEach((role) => queryParams.append("roles", role));
  }

  const requestUrl = `${API_URL}/private/catalyst/users?${queryParams.toString()}`;
  const json = await smartFetch(requestUrl, {
    method: "GET",
  });

  const parsedData = getUsersResponseSchema.parse(json);

  return parsedData;
}

export async function getArchivedUsers() {
  const requestUrl = `${API_URL}/private/catalyst/users/archived`;
  const json = await smartFetch(requestUrl, {
    method: "GET",
  });

  const parsedData = getUsersResponseSchema.parse(json);

  return parsedData;
}

export async function getMeaningfulGigsUsers() {
  const requestUrl = `${API_URL}/private/catalyst/users/meaningful-gigs`;
  const json = await smartFetch(requestUrl, {
    method: "GET",
  });

  const parsedData = getMeaningfulGigsUsersResponseSchema.parse(json);

  return parsedData;
}

export async function addUserToEnterprise(data: {
  targetUserId: string;
  role?: string;
}) {
  const { targetUserId, role } = data;
  const requestUrl = `${API_URL}/private/catalyst/users/${targetUserId}/add-to-enterprise`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
    body: JSON.stringify({ role }),
  });

  return json;
}

export async function syncDefaultMgUsers() {
  const requestUrl = `${API_URL}/private/catalyst/users/sync-default-mg-users`;
  return smartFetch(requestUrl, { method: "POST" }) as Promise<{
    added: string[];
    skipped: string[];
    notFound: string[];
  }>;
}

export async function backfillPunttAccess() {
  const requestUrl = `${API_URL}/private/catalyst/users/backfill-puntt-access`;
  return smartFetch(requestUrl, { method: "POST" }) as Promise<{
    usersProcessed: number;
    enterprisesTargeted: number;
    profilesAdded: number;
  }>;
}

export async function rollbackPunttAccess() {
  const requestUrl = `${API_URL}/private/catalyst/users/rollback-puntt-access`;
  return smartFetch(requestUrl, { method: "POST" }) as Promise<{
    usersProcessed: number;
    enterprisesCleaned: number;
    profilesRemoved: number;
  }>;
}

export async function inviteUser(data: {
  enterpriseId: string;
  name: string;
  email: string;
  role: string;
}) {
  const { enterpriseId, ...body } = data;
  const requestUrl = `${API_URL}/private/catalyst/internal/enterprises/${enterpriseId}/invite`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
    body: JSON.stringify(body),
  });

  // userSchema coerces ISO date strings (onboardedAt/lastActiveAt/etc.) —
  // do not hand-convert a subset of fields; that left existing-user invites
  // failing Zod parse when those timestamps were present.
  return userSchema.parse(json);
}

export async function updateCurrentUser(updatedAttributes: Partial<User>) {
  const requestUrl = `${API_URL}/private/users`;
  const json = await smartFetch(requestUrl, {
    method: "PUT",
    body: JSON.stringify(updatedAttributes),
  });

  const parsedData = userSchema.parse(json);

  return parsedData;
}

export async function archiveUser(userId: string) {
  const requestUrl = `${API_URL}/private/users/${userId}`;
  // Returns nothing
  await smartFetch(requestUrl, {
    method: "DELETE",
  });
}

export async function unarchiveUser(userId: string) {
  const requestUrl = `${API_URL}/private/users/${userId}/unarchive`;
  // Returns success response
  await smartFetch(requestUrl, {
    method: "POST",
  });
}

export async function archiveMeaningfulGigsUser(userId: string) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/archive`;
  const json = await smartFetch(requestUrl, {
    method: "DELETE",
  });
  return json;
}

export async function updateUserRole({
  userId,
  role,
}: {
  userId: string;
  role: string;
}) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/role/${role}`;
  const json = await smartFetch(requestUrl, {
    method: "PUT",
  });

  const parsedData = updateUserRoleResponseSchema.parse(json);

  return parsedData;
}

export async function updateUserAdminNote({
  userId,
  adminNote,
}: {
  userId: string;
  adminNote: string;
}) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/admin-note`;
  const json = await smartFetch(requestUrl, {
    method: "PUT",
    body: JSON.stringify({ adminNote }),
  });

  return updateUserAdminNoteResponseSchema.parse(json);
}

export async function resendInvite(userId: string) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/resend-invite`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
  });

  return json;
}

export async function removeUserFromEnterprise(userId: string) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/remove-from-enterprise`;
  const json = await smartFetch(requestUrl, {
    method: "DELETE",
  });
  return json;
}

export async function restoreUserToEnterprise(userId: string) {
  const requestUrl = `${API_URL}/private/catalyst/users/${userId}/restore-to-enterprise`;
  const json = await smartFetch(requestUrl, {
    method: "POST",
  });
  return json;
}

export async function getRemovedUsers() {
  const requestUrl = `${API_URL}/private/catalyst/users/removed`;
  const json = await smartFetch(requestUrl, {
    method: "GET",
  });
  const parsedData = getUsersResponseSchema.parse(json);
  return parsedData;
}

// @ts-expect-error TS2339: `window` is strictly typed
window.UnsafeDeps = Object.assign({}, window.UnsafeDeps, {
  userApis: {
    getUsers,
    getArchivedUsers,
    getMeaningfulGigsUsers,
    addUserToEnterprise,
    syncDefaultMgUsers,
    backfillPunttAccess,
    rollbackPunttAccess,
    inviteUser,
    updateCurrentUser,
    archiveUser,
    unarchiveUser,
    archiveMeaningfulGigsUser,
    updateUserRole,
    resendInvite,
    removeUserFromEnterprise,
    restoreUserToEnterprise,
    getRemovedUsers,
  },
});
